import type { Salarie } from '@shared/types/Salarie';
import type { Conge } from '@shared/types/Conge';
import { CalendrierService } from './CalendrierService';
import type { StcCongesConfig } from '@application/rh/StcConfig';
import {
  CONGES_JOURS_ANNUELS_LOI,
  calculerSoldeCongesUnifie,
} from './CongesBalance';

/**
 * Service de gestion des congés (Loi 3/94 Gabon).
 * Droits annuels : 24 jours ouvrables (2 jours par mois travaillé).
 * Congé maternité : 14 semaines (Loi 3/94 Art. 168).
 * Congé paternité : 11 jours (Loi 3/94 Art. 166).
 */
export class CongesService {
  static readonly JOURS_ANNUELS = CONGES_JOURS_ANNUELS_LOI;
  static readonly MATERNITE_SEMAINES = 14;
  static readonly PATERNITE_JOURS = 11;

  /** Calcule le nombre de jours acquis depuis la date d'embauche (2 j / mois, Loi 3/94). */
  static calculerDroitsAcquis(
    salarie: Salarie,
    dateRef: Date = new Date(),
    config?: StcCongesConfig,
  ): number {
    return calculerSoldeCongesUnifie(salarie, [], config, dateRef).acquis;
  }

  /** Solde unique (même moteur que le STC). `conges_solde` de la fiche est prioritaire. */
  static calculerSolde(
    salarie: Salarie,
    conges: readonly Conge[],
    dateRef: Date = new Date(),
    config?: StcCongesConfig,
  ): number {
    return calculerSoldeCongesUnifie(salarie, conges, config, dateRef).solde;
  }

  /** Vérifie si une demande de congé est valide (solde suffisant, dates cohérentes). */
  static valider(
    salarie: Salarie,
    conges: readonly Conge[],
    demande: Pick<Conge, 'type' | 'date_debut' | 'date_fin' | 'jours'>,
    config?: StcCongesConfig,
  ): { ok: boolean; raison?: string } {
    const debut = new Date(demande.date_debut);
    const fin = new Date(demande.date_fin);
    if (fin < debut) return { ok: false, raison: 'date_fin avant date_debut' };

    if (demande.type === 'annuel') {
      const solde = this.calculerSolde(salarie, conges, new Date(), config);
      if (demande.jours > solde)
        return { ok: false, raison: `Solde insuffisant : ${solde}j disponibles` };
    }
    return { ok: true };
  }

  /** Durée standard d'un congé maternité en jours calendaires (14 semaines). */
  static dureeMaternite(): number {
    return this.MATERNITE_SEMAINES * 7;
  }
  static dureePaternite(): number {
    return this.PATERNITE_JOURS;
  }

  /** Jours calendaires inclusifs entre deux dates ISO. */
  static compterJoursCalendaires(dateDebut: string, dateFin: string): number {
    const debut = new Date(`${dateDebut}T00:00:00.000Z`);
    const fin = new Date(`${dateFin}T00:00:00.000Z`);
    if (Number.isNaN(debut.getTime()) || Number.isNaN(fin.getTime()) || fin < debut) return 0;
    return Math.round((fin.getTime() - debut.getTime()) / 86_400_000) + 1;
  }

  /** Congé annuel : jours ouvrables. Autres types : jours calendaires. */
  static compterJours(dateDebut: string, dateFin: string, type?: Conge['type']): number {
    if (type && type !== 'annuel') return this.compterJoursCalendaires(dateDebut, dateFin);
    return CalendrierService.joursOuvrables(dateDebut, dateFin);
  }

  /** Jours annuels en demande (non encore tranchés). */
  static joursEnCours(salarie: Salarie, conges: readonly Conge[]): number {
    return conges
      .filter((c) => c.salarie_id === salarie.id && c.type === 'annuel' && c.statut === 'demande')
      .reduce((sum, c) => sum + c.jours, 0);
  }

  static joursPris(salarie: Salarie, conges: readonly Conge[]): number {
    return conges
      .filter(
        (c) =>
          c.salarie_id === salarie.id &&
          c.type === 'annuel' &&
          (c.statut === 'valide' || c.statut === 'pris'),
      )
      .reduce((sum, c) => sum + c.jours, 0);
  }

  static creerDemande(
    salarie: Salarie,
    conges: readonly Conge[],
    input: {
      tenant_id: string;
      type: Conge['type'];
      date_debut: string;
      date_fin: string;
      motif?: string;
      statut?: Conge['statut'];
      id?: string;
    },
    config?: StcCongesConfig,
  ): Conge {
    const jours = this.compterJours(input.date_debut, input.date_fin, input.type);
    if (jours <= 0) throw new Error('Période invalide');
    const statut = input.statut ?? 'demande';
    const check = this.valider(
      salarie,
      conges,
      {
        type: input.type,
        date_debut: input.date_debut,
        date_fin: input.date_fin,
        jours,
      },
      config,
    );
    if (!check.ok) throw new Error(check.raison ?? 'Demande refusée');
    const now = new Date().toISOString();
    return {
      id: input.id ?? `conge_${Date.now().toString(36)}`,
      tenant_id: input.tenant_id,
      salarie_id: salarie.id,
      type: input.type,
      date_debut: input.date_debut,
      date_fin: input.date_fin,
      jours,
      motif: input.motif,
      statut,
      ...(statut === 'valide' ? { valide_le: now } : {}),
      cree_le: now,
    };
  }

  static transiter(conge: Conge, next: Conge['statut'], actorId?: string): Conge {
    const allowed: Record<Conge['statut'], readonly Conge['statut'][]> = {
      demande: ['valide', 'refuse', 'annule'],
      valide: ['pris', 'annule'],
      refuse: [],
      pris: [],
      annule: [],
    };
    if (!allowed[conge.statut].includes(next)) {
      throw new Error(`Cette demande ne peut plus passer à « ${next} »`);
    }
    return {
      ...conge,
      statut: next,
      ...(next === 'valide' ? { valide_par: actorId, valide_le: new Date().toISOString() } : {}),
    };
  }
}
